import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Call, CallSessionParticipantJoinedEvent, CallSessionParticipantLeftEvent, StreamVideoClient } from "@stream-io/video-react-sdk";
import ScreenshareModal from "./ScreenshareModal";
import { generateScreenshareToken, initScreenshare } from "@/js/api/screenshareApi";
import { useBeforeUnload } from "react-router-dom";
import { useToast } from "@/components/ui/Toast";
import { Box } from "@chakra-ui/react";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";
import useStore from "@/stores/index";
import { generateCallId, getUserCallId, openScreenshareTabForStudents } from "./util";
import { UserDetail } from "@/stores/classroomStore_I";
import { ActionButton } from "@/js/components/Classrooms/ActionButton";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";

interface ScreenshareProps {
	open: boolean;
	handleClose: () => void;
	classroomId: string;
	users: UserDetail[];
	setModalOpen: (open: boolean) => void;
	focusedStudentIds: string[];
}

const SCREENSHARE_VIEWER_ROLE = "screenshare_viewer";

const cleanupCall = (call: Call) => {
	call
		.endCall()
		.then(() => console.debug("Call ended"))
		.catch((error) => console.error("Error ending call", error));
	call
		.leave()
		.then(() => console.debug("Left call"))
		.catch((error) => console.error("Error leaving call", error));
};

const cleanupClient = (client: StreamVideoClient) => {
	client
		.disconnectUser()
		.then(() => console.debug("User disconnected"))
		.catch((error) => console.error("Error disconnecting user", error));
};

const Screenshare = ({ open, handleClose, classroomId, users, setModalOpen, focusedStudentIds }: ScreenshareProps): React.ReactElement => {
	const [streamApiKey, setStreamApiKey] = useState<string | null>(null);
	const [client, setClient] = useState<StreamVideoClient | null>(null);
	const [call, setCall] = useState<Call | null>(null);
	const [screenshareStarted, setScreenshareStarted] = useState<boolean>(false);
	const [initialised, setInitialised] = useState<boolean>(false);
	const [callId, setCallId] = useState<string | null>(null);
	const callStarted = useMemo(() => call !== null && callId != null && initialised, [call, callId, initialised]);
	const [currentCallUsers, setCurrentCallUsers] = useState<Set<string>>(new Set());
	const [usernameUserIDMapping, setUsernameUserIDMapping] = useState<{ [key: string]: string }>({});
	const activeGroup = useUserSessionStore.getState().getActiveGroup();
	const isClassActive: boolean = activeGroup !== undefined ? useStore.getState().classroom.isClassSessionActiveV2(activeGroup) : false;

	const { errorToast } = useToast();

	useEffect(() => {
		useScreenshareStore.setState({
			screenshareStudentUsernames: currentCallUsers,
		});
	}, [currentCallUsers]);

	const reset = () => {
		setInitialised(false);
		setClient(null);
		setCall(null);
		setCallId(null);
		setScreenshareStarted(false);
		setStreamApiKey(null);
		setCurrentCallUsers(new Set<string>());
	};

	// end any ongoing call when the class ends.
	useEffect(() => {
		if (!isClassActive) {
			reset();
		}
	}, [isClassActive]);

	// call cleanup function
	useEffect(() => {
		if (!call) return;
		return () => {
			cleanupCall(call);
		};
	}, [call]);

	// client cleanup function
	useEffect(() => {
		if (!client) return;
		return () => {
			cleanupClient(client);
		};
	}, [client]);

	// try cleanup when the window / tab is closed
	useBeforeUnload(
		useCallback(() => {
			if (client) cleanupClient(client);
			if (call) cleanupCall(call);
			reset();
		}, [call, client])
	);

	const endScreenshareCallback = () => {
		reset();
		handleClose();
	};

	const addUsersToScreenshare = async (students: string[]) => {
		const teacherId = useUserSessionStore.getState().getActiveUser();
		const applianceId = useUserSessionStore.getState().getActiveDevice();
		if (teacherId == null || applianceId == null) {
			errorHandler();
			return;
		}

		// find all the users in students that haven't been added to the call yet
		const newUsers = students.filter((student) => !currentCallUsers.has(student));

		// create call ids for all new students and store them
		const newMappings: { [key: string]: string } = {};
		const studentCallIds: string[] = [];
		for (const s of newUsers) {
			const userCallId = await getUserCallId(s, applianceId);
			newMappings[userCallId] = s;
			studentCallIds.push(userCallId);
		}

		const mappings = { ...usernameUserIDMapping, ...newMappings };
		setUsernameUserIDMapping(mappings);

		if (callStarted) {
			// adding users to an already started call
			try {
				// this shouldn't happen, but is here to suppress ts errors
				if (callId == null || streamApiKey == null) {
					return;
				}

				// create tokens for the new users
				const { tokens } = await generateScreenshareToken(callId, studentCallIds, applianceId);

				// add users to the call (once the call has started the teacher has these perms available).
				await call?.updateCallMembers({
					update_members: studentCallIds.map((id) => ({ user_id: id, role: SCREENSHARE_VIEWER_ROLE })),
				});

				await openScreenshareTabForStudents(tokens, mappings, callId, streamApiKey);

				setCurrentCallUsers(new Set<string>(students));
				setModalOpen(false);
			} catch (error) {
				// do not reset the state here, as the call is still ongoing for existing users.
				console.error("Error adding users to screenshare", error);
				errorToast({
					title: "Screen Share error",
					description: "An unexpected error occurred while adding users to the screen share.",
					isClosable: true,
				});
			}
		} else {
			// call hasn't started yet
			try {
				const callId = generateCallId(classroomId);

				setCallId(callId);
				setInitialised(true);

				const teacherCallId = await getUserCallId(teacherId, classroomId);

				const { teacherToken, apiKey, studentTokens } = await initScreenshare(callId, teacherCallId, studentCallIds, applianceId);

				const newClient = new StreamVideoClient({
					apiKey: apiKey,
					user: { id: teacherCallId, name: "Teacher" },
					token: teacherToken,
				});
				const newCall = newClient.call("screen-share", callId);
				await newCall.get();
				await newCall.join({
					create: true,
				});

				setModalOpen(false);

				newCall.screenShare.setSettings({
					maxFramerate: 30,
				});

				// wait for the teacher to select the screenshare tab / window
				try {
					await newCall.screenShare.disableScreenShareAudio();
					await newCall.screenShare.enable();
				} catch (error) {
					if (error instanceof Error) {
						// we can ignore this message, as it is thrown when the teacher clicks 'Cancel' on the screenshare dialog
						if (error.message.includes("Permission denied")) {
							reset();
							return;
						}
					}

					console.error("Error enabling screenshare", error);
					errorHandler();
					return;
				}
				setScreenshareStarted(true);

				await openScreenshareTabForStudents(studentTokens, mappings, callId, apiKey);

				setStreamApiKey(apiKey);
				setCall(newCall);
				setClient(newClient);
				setCurrentCallUsers(new Set<string>(students));
			} catch (error) {
				console.error("Error starting screenshare", error);
				errorHandler();
			}
		}
	};

	// remove users from currentCallUsers when we receive a left call event
	const studentLeftCallCallback = useCallback(
		(studentId: string) => {
			const studentUsername = usernameUserIDMapping[studentId];
			if (!studentUsername) {
				return;
			}
			const filtered = [...currentCallUsers].filter((user) => user !== studentUsername);
			setCurrentCallUsers(new Set<string>(filtered));
		},
		[usernameUserIDMapping, currentCallUsers]
	);

	// add students back to the call when we receive a joined call event (e.g. after a refresh or reconnect)
	const studentJoinedCallCallback = useCallback(
		(studentId: string) => {
			const studentUsername = usernameUserIDMapping[studentId];
			if (!studentUsername || currentCallUsers.has(studentUsername)) {
				return;
			}
			setCurrentCallUsers(new Set<string>([...currentCallUsers, studentUsername]));
		},
		[usernameUserIDMapping, currentCallUsers]
	);

	useEffect(() => {
		if (call == null) return;

		// Subscribe to participant leave events
		const unsubscribeParticipantLeft = call.on(
			"call.session_participant_left",
			(event: { type: "call.session_participant_left" } & CallSessionParticipantLeftEvent) => {
				console.debug("Participant left", event.participant.user.id);
				studentLeftCallCallback(event.participant.user.id);
			}
		);
		// Subscribe to participant join events
		const unsubscribeParticipantJoined = call.on(
			"call.session_participant_joined",
			(event: { type: "call.session_participant_joined" } & CallSessionParticipantJoinedEvent) => {
				console.debug("Participant joined", event.participant.user.id);
				studentJoinedCallCallback(event.participant.user.id);
			}
		);

		// subscribe to screenshare status changes, so we can end the session when the teacher stops sharing from the
		// inbuilt 'Stop sharing' button
		const screenshareStatusSubscription = call.screenShare.state.status$.subscribe((status) => {
			if (status !== "enabled" && screenshareStarted) {
				reset();
			}
		});

		return () => {
			unsubscribeParticipantLeft();
			unsubscribeParticipantJoined();
			screenshareStatusSubscription.unsubscribe();
		};
	}, [call, studentJoinedCallCallback, studentLeftCallCallback, screenshareStarted]);

	const errorHandler = () => {
		reset();
		errorToast({
			title: "Screen Share error",
			description: "An unexpected error ocurred while sharing your screen.",
			isClosable: true,
		});
	};

	return (
		<Box>
			{callStarted && (
				<ActionButton
					label="Screen is shared"
					icon="fa-screencast"
					iconColor="#0075DB"
					handleClick={() => setModalOpen(true)}
					testId="screenshare-button"
				/>
			)}
			<ScreenshareModal
				open={open}
				close={() => setModalOpen(false)}
				endScreenshareCallback={endScreenshareCallback}
				usersSelectedCallback={addUsersToScreenshare}
				allStudents={users}
				currentCallStudentUsernames={[...currentCallUsers]}
				screenshareOngoing={callStarted}
				pausedStudentUsernames={useClassroomPolicyStore.getState().getPausedStudents()}
				focusedStudentIds={focusedStudentIds}
			/>
		</Box>
	);
};

export default Screenshare;
